import React, { useRef } from 'react';
import { Box, BoxProps, Stack, TextFieldProps, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Control, Controller } from 'react-hook-form';
import { Editor } from '@tinymce/tinymce-react';
import { FetchResult, useMutation } from '@apollo/client';
import { GetPreSignedUrlDocument, GetPreSignedUrlMutation } from '../../graphql/generated/graphql';
import { uploadFile } from '../utils/fileupload';

export type FormEditorFieldProps = TextFieldProps & {
  control: Control<any>;
  name: string;
  defaultValue?: string;
  labelComponent?: React.ReactElement;
  label?: string;
  containerProps?: BoxProps;
  onChangeInput?: (value: any) => void;
  description?: string;
  height?: number;
  menubar?: string;
  toolbar?: string;
};

/**
 * Describe your component here
 */
const FormEditorField: React.FC<FormEditorFieldProps> = (props) => {
  const {
    name,
    control,
    labelComponent,
    containerProps,
    onChangeInput,
    defaultValue = '',
    description,
    height = 300,
    menubar = "edit insert view format table tools",
    toolbar = "undo redo | formatselect | fontselect | fontsizeselect | forecolor backcolor emoticons  | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media | code | preview print | removeformat | help |",
  } = props;

  const editorRef = useRef<any>(null);

  const [getPreSignedUrlMutation] = useMutation(GetPreSignedUrlDocument);

  const fileUpload = async (file: File) => {
    const { name, type } = file;
    const presignedName = `${new Date().getTime()}-${name}`;
    let url = '';
    const { data }: FetchResult<GetPreSignedUrlMutation> =
      await getPreSignedUrlMutation({
        variables: {
          key: { id: presignedName },
        },
      });
    if (data?.GetPreSignedUrl.status) {
      url = await uploadFile({
        file: file,
        presignedUrl: `${data?.GetPreSignedUrl.data}`,
        type,
        presignedName,
      });
    }
    return { url };
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={containerProps?.sx}>
        {labelComponent}
        {
          description && (
            <Typography variant='body2' sx={{
              color: '#827a7a'
            }} mb={1} >{description}</Typography>
          )
        }
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange } }) => (
            <Stack sx={{ width: "100%" }}>
              <Editor
                apiKey='mquzuse4wxx1kxur0nae7obrrhjb1461kgo9hrbrx5fskonp'
                initialValue={defaultValue}
                onInit={(evt, editor) => editorRef.current = editor}
                init={{
                  height: height,
                  allow_script_urls: true,
                  menubar,
                  content_css: "/tinycmeCustomCSS.css",
                  content_style:
                    "@import url('https://fonts.googleapis.com/css2?family=Open+Sans&display=swap');" +
                    "@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital@0;1&amp');" +
                    "@import url('https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&amp');" +
                    "@import url('https://fonts.googleapis.com/css2?family=Sorts+Mill+Goudy:ital@0;1&amp;display=swap');" +
                    "@import url('https://db.onlinewebfonts.com/c/1d64aadb5ee4d128e7c17eeeb473aa46?family=Tibere+OT+W01+Light');" +
                    "@import url('https://db.onlinewebfonts.com/c/bb882fe840a44ef460467ef745fa505e?family=Tibere+OT+W03+Medium');" +
                    "@import url('https://db.onlinewebfonts.com/c/4d44487b9460a82d49a4297a2d8d6f87?family=LTC+Goudy+Oldstyle+W01+Italic');" +
                    "body {font-family: 'Sorts Mill Goudy'; font-size: 16pt;} .btn {--bs-btn-padding-x: 0.75rem;--bs-btn-padding-y: 0.375rem;--bs-btn-font-family: Barlow,system-ui,-apple-system,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,Liberation Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji;--bs-btn-font-size: 1rem;--bs-btn-font-weight: 600;--bs-btn-line-height: 1.5;--bs-btn-color: var(--bs-body-color);--bs-btn-bg: transparent;--bs-btn-border-width: var(--bs-border-width);--bs-btn-border-color: transparent;--bs-btn-border-radius: var(--bs-border-radius);--bs-btn-hover-border-color: transparent;--bs-btn-box-shadow: inset 0 1px 0 hsla(0,0%,100%,.15),0 1px 1px rgba(0,0,26,.075);--bs-btn-disabled-opacity: 0.65;--bs-btn-focus-box-shadow: 0 0 0 0.25rem rgba(var(--bs-btn-focus-shadow-rgb),.5);background-color: var(--bs-btn-bg);border: var(--bs-btn-border-width) solid var(--bs-btn-border-color);color: var(--bs-btn-color);cursor: pointer;display: inline-block;font-family: var(--bs-btn-font-family);font-size: var(--bs-btn-font-size);font-weight: var(--bs-btn-font-weight);line-height: var(--bs-btn-line-height);padding: var(--bs-btn-padding-y) var(--bs-btn-padding-x);text-align: center;text-decoration: none;transition: color .15s ease-in-out,background-color .15s ease-in-out,border-color .15s ease-in-out,box-shadow .15s ease-in-out;-webkit-user-select: none;-moz-user-select: none;user-select: none;vertical-align: middle;}.btn-group-lg>.btn, .btn-lg {--bs-btn-padding-y: 0.5rem;--bs-btn-padding-x: 1rem;--bs-btn-font-size: 1.25rem;--bs-btn-border-radius: var(--bs-border-radius-lg);}.btn-custom {--bs-btn-color: #fff;--bs-btn-bg: #ec4b49;--bs-btn-border-color: #e83231;--bs-btn-hover-color: #fff;--bs-btn-hover-bg: #c9403e;--bs-btn-hover-border-color: #ba2827;--bs-btn-focus-shadow-rgb: 235,81,80;--bs-btn-active-color: #fff;--bs-btn-active-bg: #bd3c3a;--bs-btn-active-border-color: #ae2625;--bs-btn-active-shadow: inset 0 3px 5px rgba(0,0,26,.125);--bs-btn-disabled-color: #00001a;--bs-btn-disabled-bg: #ec4b49;--bs-btn-disabled-border-color: #e83231;font-size: 1.875rem;padding-right: 7rem;position: relative;}.btn-custom:after {align-items: center;background-color: #e83231;bottom: 0;content: \"\f101\";display: flex;font-family: Font Awesome 6 Free;justify-content: center;position: absolute;right: 0;top: 0;width: 100px;}.widget-button-dark-outline {border: 2px solid #231f20;color: #231f20;display: block;font-family: Barlow,system-ui,-apple-system,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,Liberation Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji;font-weight: 500;padding: 10px;text-align: center;text-decoration: none;font-size: 1.75rem;}.widget-button-dark-outline:hover {color: #e83231;text-decoration: none;border: 2px solid #e83231;} " +
                    "div.image-box { width: 47%; background-color: #f9f1df; margin-right: 2.5%; display: inline-block; box-sizing: border-box; font-size: 18px; color: #231f20; vertical-align: top; }" +
                    "div.side-image { width: 100% !important;}"+
                    "div.image-box img {border: 15px solid #000; margin-top: 20px; margin-bottom: 20px;}" +
                    "img {border: 15px solid #000; margin-top: 20px; margin-bottom: 20px;}",
                    plugins: [
                    "fullscreen",
                    "help",
                    "link",
                    "media",
                    "image",
                    "table",
                    "anchor",
                    "charmap",
                    "autolink",
                    "advlist",
                    "lists",
                    "preview",
                    "hr",
                    "pagebreak",
                    "nonbreaking",
                    "searchreplace",
                    "wordcount",
                    "visualblocks",
                    "visualchars",
                    "code",
                    "insertdatetime",
                    "media",
                    "nonbreaking",
                    "table",
                    "contextmenu",
                    "directionality",
                    "emoticons",
                    "template",
                    "textcolor",
                    "colorpicker",
                    "textpattern",
                    "imagetools",
                    "codesample",
                    "print",
                    "importcss",
                  ],
                  toolbar,
                  block_formats: "Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3;Heading 4=h4;Heading 5=h5;Heading 6=h6;Div=div;Pre=pre",
                  fontsize_formats: '4pt 6pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 25pt 26pt 28pt 30pt 32pt 34pt 36pt 40pt 44pt 48pt 52pt 56pt 60pt 66pt 70pt 76pt 84pt',
                  font_formats: "Sorts Mill Goudy=Sorts Mill Goudy;Barlow=Barlow;Tibere=Tibere OT W01 Light;Goudy Italic=LTC Goudy Oldstyle W01 Italic;Goudy=LTC Goudy Oldstyle W01;Arial=arial,helvetica,sans-serif;Courier New=courier new,courier,monospace;AkrutiKndPadmini=Akpdmi-n;Verdana=Verdana;Tahoma=Tahoma;Trebuchet MS=Trebuchet MS;Times New Roman=Times New Roman;Georgia=Georgia;Garamond=Garamond;Courier New=Courier New;Lato Black=lato; Roboto=roboto;",
                  file_picker_callback: function(callback, value, meta) {
                    var input = document.createElement('input');
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*, video/*');

                    input.onchange = function () {
                      if (input && input.files) {
                        let file = input.files[0];

                        fileUpload(file).then(({url}) => {
                          callback(url);
                        });
                      }
                    };
                    input.click();
                  },
                    
                  file_picker_types: 'file image media',
                }}
                onChange={(e) => {
                  if (onChangeInput) onChangeInput(e.target.getContent());
                  onChange(e.target.getContent());
                }}
              />
            </Stack>
          )}
          defaultValue={defaultValue}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default FormEditorField;
